import { useMemo } from "react";

import Highcharts from "highcharts";
import { useFormatNumericHook } from "hooks";
import { Chart } from "store/types";
import { CHART_BIN_BY_MEASURE_PREFIX } from "utils";
import { immutablyChangeValue } from "utils/immutable";

const graphColors: string[] = [
  "#4690F5",
  "#32CED8",
  "#D428BD",
  "#496682",
  "#6E45EB",
  "#EBAF01",
  "#D93E3E",
  "#D77108",
  "#0ABB91",
  "#FEA458"
];

const commonMenuItems: string[] = [
  "viewFullscreen",
  "printChart",
  "separator",
  "downloadPNG",
  "downloadJPEG",
  "downloadPDF",
  "downloadSVG",
  "separator",
  "downloadCSV",
  "downloadXLS"
];

const getCommonTitle = (convertCamelCaseGroupBy: string): Highcharts.Options["title"] => ({
  text: `${convertCamelCaseGroupBy}`,
  align: "left",
  style: {
    fontSize: "18px",
    fontWeight: "500",
    color: "#173c4c",
    fontFamily: "Roboto"
  }
});

const getCommonExportingMenu = (): Highcharts.Options["exporting"] => ({
  buttons: {
    contextButton: {
      className: "chart-export-trigger",
      // enabled: enabled,
      enabled: false,
      menuClassName: "chart-export-menu",
      menuItems: commonMenuItems
    }
  },
  enabled: true,
  allowHTML: true,
  fallbackToExportServer: false
});

const commonLegend: Highcharts.Options["legend"] = {
  verticalAlign: "bottom",
  layout: "horizontal",
  width: "100%",
  itemStyle: {
    fontSize: "14px",
    textOverflow: "elipsis",
    fontWeight: "normal"
  },
  itemMarginTop: 1,
  padding: 5,
  maxHeight: 70,
  itemMarginBottom: 5
};

const commonChart: Highcharts.Options["chart"] = {
  plotShadow: false,
  // width: 420,
  height: 290, // Styled via scss to make take the full size on full screen
  borderRadius: 8
};

interface Params {
  chartData: Chart | undefined;
  setFieldValue: (field: string, value: any) => void;
  groupBy: string;
  chart?: Highcharts.Options["chart"];
  exporting?: Highcharts.Options["exporting"];
  tooltip?: Highcharts.Options["tooltip"];
  accessibility?: Highcharts.Options["accessibility"];
  plotOptions?: Highcharts.Options["plotOptions"];
  legend?: Highcharts.Options["legend"];
}

export const useChartHook = ({
  chartData,
  setFieldValue,
  groupBy,
  chart,
  exporting,
  tooltip,
  accessibility,
  plotOptions,
  legend
}: Params) => {
  const { title, axis, data, postfix } = chartData || {};
  const chartType = data?.type || "";
  const formatNumeric = useFormatNumericHook();
  const valuePrefix = postfix === "%" ? "" : postfix;
  const valuePostfix = postfix === "%" ? postfix : "";

  const convertCamelCaseGroupBy = useMemo(() => {
    const base = groupBy?.startsWith(CHART_BIN_BY_MEASURE_PREFIX)
      ? groupBy.slice(CHART_BIN_BY_MEASURE_PREFIX.length)
      : groupBy;
    if (base === "deal") return "deal_name";
    // if (groupBy === "assetClass") return "Asset Class";
    // if (groupBy === "subAssetClass") return "Sub Asset Class";
    // Use regular expression to insert spaces before uppercase letters
    // and then split the string by spaces
    const words = base
      ?.replace(/([A-Z])/g, " $1")
      .trim()
      .split(" ");
    // Capitalize the first letter of each word
    const capitalizedWords = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    // Join the words to form the resulting string
    return capitalizedWords?.join(" ");
  }, [groupBy]);

  const pieChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          ...chart,
          type: "pie"
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          pointFormat: `<span>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix} ({point.percentage:.2f}%)</b></span>`,
          ...tooltip
        },
        accessibility: {
          point: {
            valueSuffix: "%"
          },
          ...accessibility
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            size: 130, // to make take the full size on full screen
            center: ["50%", 60], // to make take the full size on full screen
            showInLegend: true,
            // innerSize: "10%",
            dataLabels: {
              style: {
                fontSize: "14px"
              },
              enabled: true,
              distance: 4,
              format: "{point.percentage:.1f} %"
            }
          },
          series: {
            cursor: "pointer",
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        legend: {
          ...commonLegend,
          align: "left",
          valueDecimals: 2,
          x: 0,
          y: 0,
          ...legend
        },
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        colors: graphColors,
        series: [{ ...data, type: "pie" as any }]
      },
    [
      data,
      axis,
      convertCamelCaseGroupBy,
      setFieldValue,
      chart,
      exporting,
      tooltip,
      accessibility,
      plotOptions,
      legend,
      postfix
    ]
  );

  const stackedBarChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          ...chart,
          type: "bar"
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          pointFormat: `<span style="color:{series.color}">{series.name}</span> <br>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix} ({point.percentage:.2f}%)</b></br>`,
          ...tooltip
        },
        accessibility: {
          point: {
            valueSuffix: "%"
          },
          ...accessibility
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        plotOptions: {
          series: {
            stacking: "normal",
            cursor: "pointer",
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        legend: {
          ...commonLegend,
          reversed: true,
          align: "left",
          ...legend
        },
        colors: graphColors,
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        },
        series: [...((data.data as any) || {})]
      },
    [
      data,
      axis,
      convertCamelCaseGroupBy,
      setFieldValue,
      chart,
      exporting,
      tooltip,
      accessibility,
      plotOptions,
      legend,
      postfix
    ]
  );

  const waterfallChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          ...chart,
          type: "waterfall"
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          // pointFormat: `${data.name}: <b>{point.y:,.0f}</b>`,
          pointFormat: `<span>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix} </b></span>`,
          ...tooltip
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        legend: { enabled: false },
        credits: { enabled: false },
        colors: graphColors,
        xAxis: {
          type: "category" //remove 0 from xAxis and tooltips
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        },
        plotOptions: {
          series: {
            borderWidth: 0,
            cursor: "pointer",
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        series:
          data.type === "waterfall"
            ? [
                {
                  data: (data.data[0] as any).data.map(
                    //for waterfall API sends structure of data different from rest of the types
                    (item: any, index: number) => ({
                      ...item,
                      color: graphColors[index % 10]
                    })
                  )
                }
              ]
            : [...((data.data as any) || {})]
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, postfix]
  );

  const barChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          plotShadow: false,
          type: "bar",
          borderRadius: 5,
          ...chart
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          pointFormat: `<span style="color:{series.color}">{series.name}</span> <br>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix}</b></br>`,
          ...tooltip
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        legend: {
          ...commonLegend,
          align: "right",
          layout: "horizontal",
          ...legend
        },
        colors: graphColors,
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        },
        plotOptions: {
          series: {
            cursor: "pointer",
            groupPadding: 0,
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        series: [...((data.data as any) || {})]
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, legend, postfix]
  );

  const columnStackedConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          type: "column",
          ...chart
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          pointFormat: `<span style="color:{series.color}">{series.name}</span> <br>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix} ({point.percentage:.2f}%)</b></br>`,
          ...tooltip
        },
        accessibility: {
          point: {
            valueSuffix: "%"
          }
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        plotOptions: {
          column: {
            stacking: "percent"
          },
          series: {
            stacking: "percent",
            cursor: "pointer",
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        legend: {
          ...commonLegend,
          align: "left",
          x: 0,
          y: 0,
          ...legend
        },
        colors: graphColors,
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        },
        series: [...((data.data as any) || {})]
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, legend, postfix]
  );

  const columnChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          type: "column",
          ...chart
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          pointFormat: `<span style="color:{series.color}">{series.name}</span> <br>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix}</b></br>`,
          ...tooltip
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0
          },
          series: {
            cursor: "pointer",
            groupPadding: 0,
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        legend: {
          ...commonLegend,
          align: "left",
          x: 0,
          y: 0,
          ...legend
        },
        colors: graphColors,
        series: [...((data.data as any) || {})],
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        }
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, legend, postfix]
  );

  const topChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && {
        chart: {
          ...commonChart,
          ...chart,
          type: "bar"
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          // pointFormat: "{series.name}: <b>{point.y:,.0f}</b>",
          pointFormat: `<span style="color:{series.color}">{series.name}</span> <br>${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix}</b></br>`,
          ...tooltip
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        legend: {
          ...commonLegend,
          align: "right",
          ...legend
        },
        colors: graphColors,
        xAxis: {
          categories: [""], //remove 0 from xAxis and tooltips
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: axis
          }
        },
        plotOptions: {
          series: {
            cursor: "pointer",
            groupPadding: 0,
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        series: [...((data.data as any) || {})]
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, legend, postfix]
  );

  const scatterConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data && title
        ? {
            chart: {
              ...commonChart,
              ...chart,
              type: "scatter",
              zoomType: "xy" // TS issue
            } as Highcharts.ChartOptions,
            exporting: { ...getCommonExportingMenu(), ...exporting },
            tooltip: {
              pointFormat: `<span>${title.split("-")[0]?.trim()}: ${postfix}<b>{point.x:,.2f}</b></span>
      <br><span>${title.split("-")[1]?.trim()}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix}</b></span></br>`,
              ...tooltip
            },
            accessibility: {
              point: {
                valueSuffix: "%"
              }
            },
            title: getCommonTitle(convertCamelCaseGroupBy),
            credits: {
              enabled: false
            },
            plotOptions: {
              scatter: {
                marker: {
                  radius: 6.5,
                  symbol: "circle",
                  states: {
                    hover: {
                      enabled: true,
                      lineColor: "rgb(100,100,100)"
                    }
                  }
                }
              },
              series: {
                point: {
                  events: {
                    click: function () {
                      setFieldValue(convertCamelCaseGroupBy, [this.series.name]);
                    }
                  }
                }
              },
              ...plotOptions
            },
            legend: {
              ...commonLegend,
              align: "left",
              x: 0,
              y: 0,
              ...legend
            },
            xAxis: {
              // categories: [""], //remove 0 from xAxis and tooltips
              // crosshair: true
              title: {
                text: title.split("-")[0]
              }
            },
            yAxis: {
              min: 0,
              title: {
                text: title.split("-")[1]
              }
            },
            colors: graphColors,
            series: data.data as any //there is an another data sctructure, in this case we have an TS error (options DTO is different)
          }
        : undefined,
    [data, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, legend, title, postfix]
  );

  const clusterBarChartConfig: Highcharts.Options | undefined = useMemo(
    () =>
      data &&
      data.data && {
        chart: {
          ...commonChart,
          ...chart,
          type: "bar",
          scrollablePlotArea: {
            minHeight: (data.data as any)?.length * 30,
            scrollPositionY: 0
          }
        },
        exporting: { ...getCommonExportingMenu(), ...exporting },
        tooltip: {
          headerFormat: "<span style='color:{point.color}'>{point.key}</span><br/>",
          pointFormat: `${axis}: ${valuePrefix}<b>{point.y:,.2f}${valuePostfix}</b>`,
          // pointFormat: `${axis}: <b>{point.y:,.2f} ${postfix === "%" ? postfix : ""}</b>`,
          ...tooltip
        },
        title: getCommonTitle(convertCamelCaseGroupBy),
        credits: {
          enabled: false
        },
        xAxis: {
          categories: [""], // remove 0 from xAxis and tooltips
          visible: false
        },
        yAxis: {
          title: {
            text: undefined
          },
          labels: {
            formatter: function () {
              return formatNumeric({
                value: this.value as string,
                format: {}
              });
            }
          }
        },
        plotOptions: {
          series: {
            cursor: "pointer",
            borderWidth: 0,
            point: {
              events: {
                click: function () {
                  setFieldValue(convertCamelCaseGroupBy, [this.name]);
                }
              }
            }
          },
          ...plotOptions
        },
        series: [
          {
            name: axis,
            type: "bar",
            data: (data.data as any[]).map((item) => {
              const percentage = item.data?.[0]?.y ?? 0;

              return {
                name: item.name,
                y: percentage,
                color: percentage > 0 ? "#066471" : "#D46535",
                dataLabels: {
                  enabled: true,
                  format: "{point.name}",
                  style: {
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "black",
                    textOverflow: "ellipsis"
                  },
                  align: percentage > 0 ? "left" : "right",
                  verticalAlign: "middle",
                  inside: true
                }
              };
            }),
            showInLegend: false
          }
        ],
        scrollbar: {
          enabled: true
        }
      },
    [data, axis, convertCamelCaseGroupBy, setFieldValue, chart, exporting, tooltip, plotOptions, formatNumeric, postfix]
  );

  const charts = useMemo(
    () => ({
      ["pie" as string]: pieChartConfig,
      ["bar" as string]: barChartConfig,
      ["barStacked" as string]: stackedBarChartConfig,
      ["column" as string]: columnChartConfig,
      ["columnStacked" as string]: columnStackedConfig,
      ["waterfall" as string]: waterfallChartConfig,
      ["top5" as string]: topChartConfig,
      ["scatter" as string]: scatterConfig,
      ["clusterBar" as string]: clusterBarChartConfig,
      "": undefined
    }),
    [
      pieChartConfig,
      barChartConfig,
      stackedBarChartConfig,
      columnChartConfig,
      columnStackedConfig,
      waterfallChartConfig,
      topChartConfig,
      scatterConfig,
      clusterBarChartConfig
    ]
  );

  return useMemo(() => {
    const selectedChart = charts[chartType];
    if (!selectedChart) return undefined;
    return immutablyChangeValue(selectedChart, ["title", "text"], title);
  }, [charts, chartType, title]);
};
